import type { Masthead, MastheadArtwork, MastheadArtworkDef, ResolvedMasthead } from '@shared/types/Masthead.types';
import { URLS } from '@shared/utils/urls';

// Membership-table catalog: one row per artwork, colorway members inside, `lead` fronts the
// admin picker tile. Shared fields (placement/dims) live once per artwork. Interim entries
// until the final design set lands; swatchColor values are placeholders until the final
// palette. The BE stores only mastheadId (uuid); slug names the S3 asset.
// S3 naming: mastheads/{slug}.png
const MASTHEAD_CATALOG: ReadonlyArray<MastheadArtworkDef> = [
  {
    artworkId: 'art_disco_ball',
    placement: 'top',
    width: 1600,
    height: 600,
    lead: 'art_masthead_disco_ball_red',
    colorways: [{ mastheadId: '1d89b319-21c7-4ff2-af9c-6189ebcdbe5d', slug: 'art_masthead_disco_ball_red', swatchColor: '#EC2E38' }],
  },
  {
    artworkId: 'top_art_chandelier',
    placement: 'top',
    width: 1600,
    height: 600,
    lead: 'art_masthead_chandelier_blue',
    colorways: [{ mastheadId: '49e0bb77-a3c4-4193-9c2a-d65d30b0a66d', slug: 'art_masthead_chandelier_blue', swatchColor: '#2B5397' }],
  },
  {
    artworkId: 'ornaments',
    placement: 'top',
    width: 1600,
    height: 600,
    lead: 'art_masthead_ornament_blue',
    colorways: [{ mastheadId: '4d7349a6-b0d0-4f97-a952-a8d49f49cbd2', slug: 'art_masthead_ornament_blue', swatchColor: '#2142A7' }],
  },
  {
    artworkId: 'orange',
    placement: 'top',
    width: 1600,
    height: 600,
    lead: 'art_masthead_orange_orange',
    colorways: [{ mastheadId: 'f446b4d8-b4a5-4215-9d5b-0e5599793dde', slug: 'art_masthead_orange_orange', swatchColor: '#F6A940' }],
  },
  {
    artworkId: 'dots',
    placement: 'center',
    width: 1600,
    height: 600,
    lead: 'art_masthead_dots_blue',
    colorways: [{ mastheadId: '15df3182-cc93-4115-a72a-d8be2d352764', slug: 'art_masthead_dots_blue', swatchColor: '#2E77BC' }],
  },
  {
    artworkId: 'spanish_tile',
    placement: 'center',
    width: 1600,
    height: 600,
    lead: 'art_masthead_spanish_tile_green',
    colorways: [{ mastheadId: 'f3449bda-40de-4f02-9357-1dc55f0d965b', slug: 'art_masthead_spanish_tile_green', swatchColor: '#ADD1B0' }],
  },
  {
    artworkId: 'stripes',
    placement: 'center',
    width: 1600,
    height: 600,
    lead: 'art_masthead_stripes_green',
    colorways: [{ mastheadId: '311d4c18-2e0b-4206-a82e-40683dbd836b', slug: 'art_masthead_stripes_green', swatchColor: '#A1CAC6' }],
  },
  {
    artworkId: 'wildflowers',
    placement: 'center',
    width: 1600,
    height: 600,
    lead: 'art_masthead_wildflowers_red',
    colorways: [{ mastheadId: '69cb5562-e079-48a7-8923-d3402d9ad163', slug: 'art_masthead_wildflowers_red', swatchColor: '#D94347' }],
  },
];

/** Catalog hydrated per artwork (colorways carry the artwork's shared fields) — drives the admin picker grid. */
export const MASTHEAD_ARTWORKS: ReadonlyArray<MastheadArtwork> = MASTHEAD_CATALOG.map(({ artworkId, placement, width, height, lead, colorways }) => {
  const hydrated: ReadonlyArray<Masthead> = colorways.map(colorway => ({ ...colorway, artworkId, placement, width, height }));
  return {
    artworkId,
    placement,
    lead: hydrated.find(colorway => colorway.slug === lead) ?? hydrated[0],
    colorways: hydrated,
  };
});

/** Flat list of every selectable colorway. */
export const MASTHEADS: ReadonlyArray<Masthead> = MASTHEAD_ARTWORKS.flatMap(artwork => artwork.colorways);

// Indexed by mastheadId (the canonical BE-stored uuid) and by slug, so template previews
// and the preview-bus can look up by the readable slug.
const mastheadsMap = new Map(MASTHEADS.flatMap(masthead => [[masthead.mastheadId, masthead] as const, [masthead.slug, masthead] as const]));

/**
 * Resolve a stored mastheadId (or slug) into a renderable accent. Returns
 * null when the accent is off or the id doesn't validate against the catalog (render nothing).
 */
export const resolveMasthead = (mastheadId: Maybe<string>): ResolvedMasthead | null => {
  const masthead = mastheadId ? mastheadsMap.get(mastheadId) : undefined;
  if (!masthead) {
    return null;
  }
  return {
    masthead,
    assetUrl: URLS.masthead(masthead.slug),
  };
};
